// Overview: headline metrics, weekly trend, current-round submission health, a
// merged activity feed and a per-league summary. The "at a glance" landing.
import {
  ActivityIcon,
  EmptyState,
  PageTitle,
  Panel,
  Pill,
  ProgressBar,
  StatCard,
  TrendChart,
  timeAgo,
  useAdminData
} from './parts';
import ForceUpdatePanel from './ForceUpdatePanel';
import OtaReleasePanel from './OtaReleasePanel';

export default function Overview() {
  const { data } = useAdminData();
  const { stats, activity, trend } = data;
  const leagues = [...data.leagues].sort((a, b) => b.memberCount - a.memberCount);
  const liveRounds = leagues.filter((l) => l.currentRound);
  const hasTrend = trend.some((t) => t.signups > 0 || t.picks > 0);

  return (
    <>
      <PageTitle title="Overview" sub="Activity across HeadToHead" />

      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <StatCard label="Users" value={stats.totalUsers} sub={`+${stats.newUsers7d} this week`} />
        <StatCard label="Picks" value={stats.totalPicks} sub={`+${stats.picks7d} this week`} accent />
        <StatCard label="Active players" value={stats.usersWithPicks} sub={`of ${stats.totalUsers} users`} />
        <StatCard label="Active leagues" value={stats.activeLeagues} sub={`of ${stats.totalLeagues} total`} />
      </div>

      <div className="mt-5 grid gap-4 lg:grid-cols-2">
        <ForceUpdatePanel />
        <OtaReleasePanel />
      </div>

      <div className="mt-5 grid gap-4 lg:grid-cols-3">
        {hasTrend && (
          <Panel title="Last 8 weeks" className="lg:col-span-2" bodyClassName="px-4 py-4 md:px-5">
            <TrendChart data={trend} />
          </Panel>
        )}

        <Panel title="Current round" className={hasTrend ? '' : 'lg:col-span-3'}>
          {liveRounds.length === 0 ? (
            <EmptyState>No open rounds right now.</EmptyState>
          ) : (
            <ul className="divide-y divide-black/5">
              {liveRounds.map((l) => {
                const r = l.currentRound!;
                const rate = r.submissionRate;
                return (
                  <li key={l.leagueId} className="px-4 py-3 md:px-5">
                    <div className="flex items-center justify-between gap-2">
                      <div className="min-w-0">
                        <div className="truncate text-[13px] font-bold">{l.name}</div>
                        <div className="text-[12px] text-ink2">{r.label}</div>
                      </div>
                      <span className="num shrink-0 text-[13px] font-semibold text-ink2">
                        {r.submitted}/{r.eligibleMembers || '—'}
                        {rate != null && <span className="ml-1 text-ink3">({Math.round(rate * 100)}%)</span>}
                      </span>
                    </div>
                    {rate != null && (
                      <div className="mt-2">
                        <ProgressBar value={rate} />
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </Panel>
      </div>

      <div className="mt-4 grid gap-4 lg:grid-cols-3">
        <Panel title="Recent activity" className="lg:col-span-2" bodyClassName="">
          {activity.length === 0 ? (
            <EmptyState>No activity yet.</EmptyState>
          ) : (
            <ul className="divide-y divide-black/5">
              {activity.map((a, i) => (
                <li key={`${a.kind}-${a.userId}-${a.at}-${i}`} className="flex items-center gap-3 px-4 py-3 md:px-5">
                  <ActivityIcon kind={a.kind} />
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-[13px]">
                      <span className="font-bold">{a.displayName}</span>{' '}
                      <span className="text-ink2">{a.detail}</span>
                    </div>
                  </div>
                  <span className="shrink-0 text-[12px] text-ink3">{timeAgo(a.at)}</span>
                </li>
              ))}
            </ul>
          )}
        </Panel>

        <Panel title="Leagues">
          {leagues.length === 0 ? (
            <EmptyState>No leagues configured.</EmptyState>
          ) : (
            <ul className="divide-y divide-black/5">
              {leagues.map((l) => (
                <li key={l.leagueId} className="flex items-center justify-between gap-3 px-4 py-3 md:px-5">
                  <div className="min-w-0">
                    <div className="truncate text-[13px] font-bold">{l.name}</div>
                    <div className="text-[12px] text-ink2">
                      {l.memberCount} members · {l.entriesCount} picks
                    </div>
                  </div>
                  {l.currentRoundId ? <Pill tone="accent">Active</Pill> : <Pill>Idle</Pill>}
                </li>
              ))}
            </ul>
          )}
        </Panel>
      </div>
    </>
  );
}
